import filter from "lodash/filter";
import includes from "lodash/includes";
import { useTemplate } from "PFApp/hooks";
import { activityCustomTypesAccessLevels } from "PFCore/helpers/custom_types";
import { getVisibleProperties } from "PFCore/helpers/templates";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import {
  AccessLevel,
  Activity,
  Engagement,
  FeatureFlag,
  Importance,
  ParsedSchemaProperty,
  Profile,
  Role
} from "PFTypes";
import { useCallback, useMemo } from "react";

import { extractValuesWithTypes } from "./custom_fields";

export const useRoleName = () => {
  const isEnabled = useIsFeatureEnabled();
  const showRoleId = isEnabled(FeatureFlag.ShowActivityId);

  const getRoleName = useCallback(
    (role) => {
      const name = role.name || role.title;
      if (showRoleId) {
        return `${role.id} - ${name}`;
      } else {
        return name;
      }
    },
    [showRoleId]
  );

  return getRoleName;
};

export const useActivityNameAndId = () => {
  const isEnabled = useIsFeatureEnabled();
  const showRoleId = isEnabled(FeatureFlag.ShowActivityId);

  const getRoleNameId = useCallback(
    (role) => {
      const name = role.name || role.title;

      return { name: name, id: showRoleId ? role.id : null };
    },
    [showRoleId]
  );

  return getRoleNameId;
};

type UseMatchableTypes = {
  templateId: number;
  levels?: AccessLevel[];
  activityCreator: Profile | null;
};

export const useMatchableTypes = ({
  templateId,
  levels = [AccessLevel.ReadWrite],
  activityCreator
}: UseMatchableTypes) => {
  const { data: currentProfile } = useCurrentProfile();
  const { customTypes } = useCustomTypes();

  const template = useTemplate(templateId);

  const matchableTypes = useMemo(() => {
    const visibleProperties = getVisibleProperties(template);

    const accessLevels = activityCustomTypesAccessLevels(currentProfile, activityCreator);
    return visibleProperties.filter((element) => {
      if (element.type === "custom_field" && !!element.match) {
        const customType = customTypes.find((type) => type.name === element.name);
        return customType && includes(levels, accessLevels[customType.id]);
      } else {
        return false;
      }
    });
  }, [template?.id, customTypes, currentProfile, activityCreator]);

  return matchableTypes;
};

export const isOpen = (activity: Activity | Role | Engagement) =>
  [null, "new", "review"].includes(activity.state);

export const isUserParticipating = (activity: Activity, id) =>
  !!activity.participants?.map(({ profile }) => profile).find((profile) => profile.id === id);

export const getCustomFieldValues = (activity: Activity, options?: { importance?: Importance }) => {
  // does not work with v1
  let customValues = extractValuesWithTypes(activity.custom_fields);
  /* eslint-disable no-prototype-builtins */
  if (options && options.hasOwnProperty("importance")) {
    customValues = filter(customValues, { importance: options.importance });
  }
  return customValues.sort((aValue, bValue) => {
    if (aValue.importance === bValue.importance) {
      return 0;
    } else if ((aValue.importance || 0) > (bValue.importance || 0)) {
      return -1;
    } else {
      return 1;
    }
  });
};

// Compliance fields should not be edited/removed
export const canEditOrRemoveMatchableTypeValues = (
  matchableType: ParsedSchemaProperty,
  matchableTypes: ParsedSchemaProperty[]
): boolean => {
  const matchablePermittedToEditOrRemoveNames = matchableTypes
    .filter(({ compliance_field }) => !compliance_field)
    .map(({ name }) => name);

  return matchablePermittedToEditOrRemoveNames.includes(matchableType.name);
};

// We need to have different logic to allow KPMG Uk to add new grades
// but we want to keep them from removing, hence we need getMatchablePermittedTypeNames
// and getMatchablePermittedAddTypeNames
export const canAddMatchableTypeValue = (
  matchableType: ParsedSchemaProperty,
  matchableTypes: ParsedSchemaProperty[]
) => {
  const matchablePermittedToAddNames = matchableTypes
    .filter(({ compliance_field, name }) => !compliance_field || name === "grade")
    .map(({ name }) => name);

  return matchablePermittedToAddNames.includes(matchableType.name);
};
